// steps/ContactStep.tsx
import { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { POSTING_PERMISSIONS, LIMITS } from "@shared/commission";
import { useCommissionStore } from "../../stores/commissionStore";
import { useIntakeCopy } from "../../i18n/useIntakeCopy";
import { honeypot } from "../../lib/honeypot";
import { ChipGroup } from "../ChipGroup";
import { StepNav } from "../StepNav";
import type { IntakeArtist, PostingPermission } from "../../types";
import { cn } from "@/lib/utils";

import { CLIENT_EMAIL_RE as EMAIL_RE } from "@shared/commission";
const field = "w-full rounded-lg border bg-[var(--color-bg)] px-3 py-2.5 text-[var(--color-text)] outline-none focus:border-[var(--color-primary)]";

export function ContactStep({ artist }: { artist: IntakeArtist }) {
  const { c, fmt } = useIntakeCopy();
  const s = useCommissionStore();
  const [touched, setTouched] = useState({ name: false, email: false });
  const nameErr = touched.name && !s.clientName.trim();
  const emailErr = touched.email && !EMAIL_RE.test(s.clientEmail.trim());
  const canNext = !!s.clientName.trim() && EMAIL_RE.test(s.clientEmail.trim()) && !!s.postingPermission;

  const Err = ({ show, text }: { show: boolean; text: string }) => (
    <AnimatePresence>{show && <m.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-1 text-xs text-[var(--color-error)]">{text}</m.p>}</AnimatePresence>
  );

  return (
    <section className="space-y-6">
      <h2 data-step-heading tabIndex={-1} className="font-(family-name:--font-display) font-normal text-3xl text-[var(--color-text)] outline-none">{fmt(c.contact.question, { artist: artist.name })}</h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-sm text-[var(--color-text-muted)]">{c.contact.name}</span>
          <input className={cn(field, nameErr ? "border-[var(--color-error)]" : "border-[var(--color-border)]")} autoComplete="name" maxLength={LIMITS.clientName} aria-label={c.contact.name}
            value={s.clientName} onChange={(e) => s.setContact({ clientName: e.target.value })} onBlur={() => setTouched((t) => ({ ...t, name: true }))} />
          <Err show={nameErr} text={c.contact.errors.name} />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-[var(--color-text-muted)]">{c.contact.email}</span>
          <input type="email" className={cn(field, emailErr ? "border-[var(--color-error)]" : "border-[var(--color-border)]")} autoComplete="email" maxLength={254} aria-label={c.contact.email}
            value={s.clientEmail} onChange={(e) => s.setContact({ clientEmail: e.target.value })} onBlur={() => setTouched((t) => ({ ...t, email: true }))} />
          <Err show={emailErr} text={c.contact.errors.email} />
          {!emailErr && <p className="mt-1 text-xs text-[var(--color-text-muted)]">{c.contact.emailHint}</p>}
        </label>
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-sm text-[var(--color-text-muted)]">{c.contact.discord}</span>
          <input className={cn(field, "border-[var(--color-border)]")} maxLength={LIMITS.discord} aria-label={c.contact.discord}
            value={s.clientDiscord} onChange={(e) => s.setContact({ clientDiscord: e.target.value })} />
        </label>
        {/* Honeypot — hidden from humans and assistive tech */}
        <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
          className="absolute left-[-9999px] h-0 w-0 opacity-0" onChange={(e) => { honeypot.current = e.target.value; }} />
      </div>

      <div>
        <h3 className="mb-3 text-lg font-medium text-[var(--color-text)]">{fmt(c.contact.posting, { artist: artist.name })}</h3>
        <ChipGroup<PostingPermission> single label={c.contact.posting}
          options={POSTING_PERMISSIONS.map((p) => ({ id: p, label: c.contact.post[p] }))}
          value={s.postingPermission ? [s.postingPermission] : []} onToggle={s.setPostingPermission} />
      </div>

      <StepNav canNext={canNext} />
    </section>
  );
}
